import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import IncomingCoreReceiveMobile from "mobile/pages/warehouse/IncomingCoreReceiveMobile";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";

import { api, get, post, put } from "lib/api";
import { fetchPriorReceivedQuantitiesByPoItem } from "services/purchaseReceipts";
import { useAuth } from "context/AuthContext";
import InfoCard from "mobile/components/InfoCard";
import MobileTopBar from "mobile/components/MobileTopBar";

const normalize = (d) =>
  !d
    ? null
    : d.attributes
    ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes }
    : { ...d, documentId: d.documentId || d.id };

const normalizeArray = (rel) => {
  if (!rel) return [];
  if (Array.isArray(rel)) return rel.map(normalize);
  if (Array.isArray(rel?.data)) return rel.data.map(normalize);
  return [];
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");

const fileUrl = (file) => {
  const raw = String(file?.url || "").trim();
  if (!raw) return "";
  try {
    return new URL(raw, api.defaults.baseURL || window.location.origin).toString();
  } catch (_e) {
    return raw;
  }
};

const isImageFile = (file) => {
  const mime = String(file?.mime || file?.type || "").toLowerCase();
  const name = String(file?.name || file?.url || "").toLowerCase();
  return mime.startsWith("image/") || /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(name);
};

const getUserName = (user) =>
  user?.name || user?.username || user?.email || user?.attributes?.name || user?.attributes?.username || user?.attributes?.email || "";

function PhotoUploadButton({ label, disabled, inputRef, onFiles }) {
  return (
    <Button
      component="label"
      variant="outlined"
      fullWidth
      disabled={disabled}
      startIcon={<Icon>photo_camera</Icon>}
      sx={{ justifyContent: "flex-start", minHeight: 46 }}
    >
      {label}
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length) onFiles(files);
        }}
      />
    </Button>
  );
}

PhotoUploadButton.propTypes = {
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  inputRef: PropTypes.shape({ current: PropTypes.object }),
  onFiles: PropTypes.func.isRequired,
};

export default function WarehouseReceivingDetailMobile() {
  const { documentId } = useParams();
  if (String(documentId || "").trim().startsWith("cr-")) {
    return <IncomingCoreReceiveMobile />;
  }

  return <PurchaseReceiptReceivingDetail documentId={documentId} />;
}

function PurchaseReceiptReceivingDetail({ documentId }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const packingInputRef = useRef(null);
  const goodsInputRef = useRef(null);
  const initializedRef = useRef(false);

  const [receivedBy, setReceivedBy] = useState("");
  const [receiptLines, setReceiptLines] = useState({});
  const [savingLines, setSavingLines] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-warehouse-receipt", documentId],
    queryFn: () =>
      get(`/api/purchase-receipts/${documentId}`, {
        publicationState: "preview",
        populate: {
          attachments: true,
          purchaseOrder: {
            populate: {
              vendor: { fields: ["name"] },
              items: {
                populate: { inventoryItem: { fields: ["sku", "name"] } },
                fields: ["quantity", "unitCost", "id", "documentId"],
              },
            },
            fields: ["purchaseOrderNumber", "id", "documentId"],
          },
          lines: {
            populate: { purchaseOrderItem: { fields: ["id", "documentId"] } },
            fields: ["expectedQuantity", "receivedQuantity", "varianceNote", "id", "documentId"],
          },
        },
      }),
    enabled: !!documentId,
    refetchOnWindowFocus: false,
  });

  const receipt = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);
  const purchaseOrder = useMemo(() => normalize(receipt?.purchaseOrder?.data || receipt?.purchaseOrder || null), [receipt]);
  const poItems = useMemo(
    () =>
      normalizeArray(purchaseOrder?.items).map((item) => ({
        ...item,
        inventoryItem: normalize(item?.inventoryItem?.data || item?.inventoryItem || null),
      })),
    [purchaseOrder]
  );
  const existingLines = useMemo(() => normalizeArray(receipt?.lines), [receipt]);
  const attachments = useMemo(() => normalizeArray(receipt?.attachments), [receipt]);
  const imageAttachments = useMemo(() => attachments.filter(isImageFile), [attachments]);
  const isEditable = receipt?.status === "draft";
  const poDocId = purchaseOrder?.documentId || purchaseOrder?.id;

  const { data: priorReceivedMap, isFetched: priorReceivedFetched } = useQuery({
    queryKey: ["m-po-prior-received-qty-wh", poDocId, documentId, receipt?.documentId],
    queryFn: async () => {
      try {
        return await fetchPriorReceivedQuantitiesByPoItem(poDocId, receipt?.documentId || documentId);
      } catch (_e) {
        return {};
      }
    },
    enabled: !!poDocId && !!documentId,
  });

  useEffect(() => {
    initializedRef.current = false;
    setReceiptLines({});
    setReceivedBy("");
  }, [documentId]);

  useEffect(() => {
    if (!receipt || !poItems.length) return;
    if (!priorReceivedFetched && poDocId) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    const nextLines = {};
    existingLines.forEach((line) => {
      const poItemRef = line.purchaseOrderItem?.documentId || line.purchaseOrderItem?.id || line.purchaseOrderItem;
      if (poItemRef == null) return;
      nextLines[String(poItemRef)] = {
        receivedQuantity: String(line.receivedQuantity ?? 0),
        varianceNote: line.varianceNote || "",
      };
    });

    poItems.forEach((poItem) => {
      const key = String(poItem.documentId || poItem.id);
      if (nextLines[key]) return;
      const ordered = parseFloat(poItem.quantity || 0) || 0;
      const already = parseFloat(priorReceivedMap?.[key] || priorReceivedMap?.[String(poItem.id)] || 0) || 0;
      nextLines[key] = {
        receivedQuantity: String(Math.max(0, ordered - already)),
        varianceNote: "",
      };
    });

    setReceiptLines(nextLines);
  }, [receipt, poItems, existingLines, priorReceivedFetched, priorReceivedMap, poDocId]);

  useEffect(() => {
    if (receipt?.receivedBy) {
      setReceivedBy(receipt.receivedBy);
      return;
    }
    const name = getUserName(user);
    if (name && !receivedBy) setReceivedBy(String(name).split("@")[0]);
  }, [receipt?.receivedBy, user]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["m-warehouse-receipt", documentId] });
    queryClient.invalidateQueries({ queryKey: ["warehouse-receipt", documentId] });
  };

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      const receiptId = receipt?.documentId || documentId;
      if (!receiptId) throw new Error("Receipt is not ready yet.");
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const uploadResponse = await api.post("/api/upload", formData);
      const uploaded = Array.isArray(uploadResponse?.data) ? uploadResponse.data : [];
      const existingIds = attachments.map((file) => file?.id).filter(Boolean);
      const nextIds = Array.from(new Set([...existingIds, ...uploaded.map((file) => file?.id).filter(Boolean)]));
      await put(`/api/purchase-receipts/${receiptId}`, { data: { attachments: nextIds } });
      return uploaded;
    },
    onSuccess: () => {
      if (packingInputRef.current) packingInputRef.current.value = "";
      if (goodsInputRef.current) goodsInputRef.current.value = "";
      invalidate();
    },
    onError: (e) => alert(e?.message || "Failed to upload receiving photos."),
  });

  const saveLineMutation = useMutation({
    mutationFn: async ({ poItemId, lineData }) => {
      const poItemIdStr = String(poItemId);
      const poItem = poItems.find((item) => String(item.documentId || item.id) === poItemIdStr);
      if (!poItem) throw new Error("PO item not found.");

      const latestData = queryClient.getQueryData(["m-warehouse-receipt", documentId]);
      const latestReceipt = latestData?.data ? normalize(latestData.data) : receipt;
      const latestLines = normalizeArray(latestReceipt?.lines);
      const existingLine = latestLines.find((line) => {
        const linePoItem = line.purchaseOrderItem?.documentId || line.purchaseOrderItem?.id || line.purchaseOrderItem;
        return String(linePoItem) === poItemIdStr;
      });

      const unitCostExpected = parseFloat(poItem.unitCost || 0) || 0;
      const payload = {
        expectedQuantity: parseFloat(poItem.quantity || 0) || 0,
        receivedQuantity: parseFloat(lineData.receivedQuantity || 0) || 0,
        unitCostExpected,
        unitCostActual: unitCostExpected,
        varianceNote: (lineData.varianceNote || "").trim() || null,
        receipt: receipt?.documentId || documentId,
        purchaseOrderItem: poItem.documentId || poItem.id,
        inventoryItem: poItem.inventoryItem?.documentId || poItem.inventoryItem?.id || poItem.inventoryItem,
      };

      if (existingLine) {
        return put(`/api/purchase-receipt-lines/${existingLine.documentId || existingLine.id}`, { data: payload });
      }
      return post("/api/purchase-receipt-lines", { data: payload });
    },
  });

  const updateReceiptMutation = useMutation({
    mutationFn: (payload) => put(`/api/purchase-receipts/${receipt?.documentId || documentId}`, { data: payload }),
    onSuccess: invalidate,
  });

  const saveAllLines = async () => {
    const entries = Object.entries(receiptLines);
    for (const [poItemId, lineData] of entries) {
      // eslint-disable-next-line no-await-in-loop
      await saveLineMutation.mutateAsync({ poItemId, lineData });
      // eslint-disable-next-line no-await-in-loop
      await queryClient.refetchQueries({ queryKey: ["m-warehouse-receipt", documentId] });
    }
  };

  const handleSubmit = async () => {
    if (!isEditable || savingLines) return;
    if (!receivedBy.trim()) {
      alert("Enter who received the shipment.");
      return;
    }
    const receivedCount = Object.values(receiptLines).reduce(
      (total, line) => total + (parseFloat(line?.receivedQuantity || 0) || 0),
      0
    );
    const hasAnyQty = Object.values(receiptLines).some((line) => (parseFloat(line?.receivedQuantity || 0) || 0) > 0);
    if (!hasAnyQty) {
      alert("Enter at least one received quantity greater than zero.");
      return;
    }

    try {
      setSavingLines(true);
      await saveAllLines();
      await updateReceiptMutation.mutateAsync({
        receivedBy: receivedBy.trim(),
        receivedAt: new Date().toISOString(),
        receivedCount,
        status: "in_review",
      });
      await queryClient.refetchQueries({ queryKey: ["m-warehouse-receipt", documentId] });
    } catch (e) {
      alert(e?.message || "Failed to submit receiving ticket.");
    } finally {
      setSavingLines(false);
    }
  };

  if (isLoading) return <LinearProgress />;

  if (error || !receipt) {
    return (
      <Box sx={{ px: 1, py: 2 }}>
        <MobileTopBar title="Receiving" onBack={() => navigate("/m/warehouse/receiving")} />
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Error loading receiving ticket.
        </Typography>
      </Box>
    );
  }

  const busy = savingLines || uploadMutation.isPending || saveLineMutation.isPending || updateReceiptMutation.isPending;

  return (
    <Box sx={{ px: 1, pb: 2 }}>
      <MobileTopBar
        title="Receive PO"
        subtitle={receipt.ticketNumber || `Receipt #${receipt.id}`}
        onBack={() => navigate("/m/warehouse/receiving")}
        right={<Chip size="small" variant="outlined" label={receipt.status || "draft"} />}
      />

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <InfoCard
          title="Shipment"
          rows={[
            { label: "PO #", value: purchaseOrder?.purchaseOrderNumber || "—" },
            { label: "Vendor", value: purchaseOrder?.vendor?.name || "—" },
            { label: "Created", value: formatDate(receipt.createdAt) },
            { label: "Photos", value: `${imageAttachments.length} image${imageAttachments.length === 1 ? "" : "s"}` },
          ]}
        />

        {isEditable ? (
          <Alert severity="info" variant="outlined">
            Photos are optional. Add them when they help document packaging, condition, or variances.
          </Alert>
        ) : null}

        <Card variant="outlined">
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" fontWeight={900}>
              Photos
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              Use the rear camera for the packing list or what physically came in when needed.
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              <PhotoUploadButton
                label={uploadMutation.isPending ? "Uploading..." : "Add packing list photo"}
                disabled={!isEditable || uploadMutation.isPending}
                inputRef={packingInputRef}
                onFiles={(files) => uploadMutation.mutate(files)}
              />
              <PhotoUploadButton
                label={uploadMutation.isPending ? "Uploading..." : "Add received goods photo"}
                disabled={!isEditable || uploadMutation.isPending}
                inputRef={goodsInputRef}
                onFiles={(files) => uploadMutation.mutate(files)}
              />
            </Stack>
            {uploadMutation.isPending ? <LinearProgress sx={{ mt: 1 }} /> : null}
            {imageAttachments.length ? (
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, overflowX: "auto", pb: 0.5 }}>
                {imageAttachments.map((file) => (
                  <Avatar
                    key={file.id || file.url || file.name}
                    variant="rounded"
                    src={fileUrl(file)}
                    alt={file.name || "Receiving photo"}
                    sx={{ width: 72, height: 72, flex: "0 0 auto", borderRadius: 1 }}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>
        </Card>

        <Card variant="outlined">
          <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
              <Typography variant="subtitle2" fontWeight={900}>
                Counts
              </Typography>
              <Chip size="small" variant="outlined" label={`${poItems.length} lines`} />
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.75}>
              {poItems.map((poItem) => {
                const poItemId = String(poItem.documentId || poItem.id);
                const line = receiptLines[poItemId] || { receivedQuantity: String(poItem.quantity || 0), varianceNote: "" };
                const inv = poItem.inventoryItem || {};
                const expected = parseFloat(poItem.quantity || 0) || 0;
                const received = parseFloat(line.receivedQuantity || 0) || 0;
                const hasVariance = Number.isFinite(received) && received !== expected;
                return (
                  <Card key={poItemId} variant="outlined" sx={{ bgcolor: "background.default" }}>
                    <Box sx={{ p: 1 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.75}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={900} noWrap>
                            {inv.sku || "Item"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.25,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              wordBreak: "break-word",
                            }}
                          >
                            {inv.name || "No description"}
                          </Typography>
                        </Box>
                        <Chip size="small" variant="outlined" color={hasVariance ? "warning" : "default"} label={`Expected ${expected}`} />
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                        <TextField
                          label="Received"
                          type="number"
                          value={line.receivedQuantity}
                          disabled={!isEditable}
                          onChange={(event) =>
                            setReceiptLines((prev) => ({
                              ...prev,
                              [poItemId]: { ...(prev[poItemId] || {}), receivedQuantity: event.target.value },
                            }))
                          }
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 128 }}
                        />
                        <IconButton
                          aria-label="Set full quantity"
                          disabled={!isEditable}
                          onClick={() =>
                            setReceiptLines((prev) => ({
                              ...prev,
                              [poItemId]: { ...(prev[poItemId] || {}), receivedQuantity: String(expected) },
                            }))
                          }
                        >
                          <Icon fontSize="small">done_all</Icon>
                        </IconButton>
                      </Stack>
                      <TextField
                        label="Variance note"
                        fullWidth
                        value={line.varianceNote}
                        disabled={!isEditable}
                        onChange={(event) =>
                          setReceiptLines((prev) => ({
                            ...prev,
                            [poItemId]: { ...(prev[poItemId] || {}), varianceNote: event.target.value },
                          }))
                        }
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Card>
                );
              })}
              {!poItems.length ? (
                <Typography variant="body2" color="text.secondary">
                  No items in this purchase order.
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </Card>

        <Card variant="outlined">
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" fontWeight={900}>
              Receiver
            </Typography>
            <TextField
              label="Received by"
              fullWidth
              value={receivedBy}
              disabled={!isEditable}
              onChange={(event) => setReceivedBy(event.target.value)}
              sx={{ mt: 1 }}
            />
            {isEditable ? (
              <Button
                variant="contained"
                fullWidth
                disabled={busy}
                onClick={handleSubmit}
                startIcon={<Icon>inventory_2</Icon>}
                sx={{ mt: 1.25, minHeight: 48 }}
              >
                {busy ? "Saving..." : "Submit to Purchasing"}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This receiving ticket has already been sent to Purchasing.
              </Typography>
            )}
            {busy ? <LinearProgress sx={{ mt: 1 }} /> : null}
          </Box>
        </Card>
      </Stack>
    </Box>
  );
}

PurchaseReceiptReceivingDetail.propTypes = {
  documentId: PropTypes.string,
};
